import { motion } from "framer-motion";

/**
 * BrandManifesto — on-brand (dark teal + lime/cyan) but visually distinctive
 * through composition:
 *
 *   • Giant "everything." word treatment — larger than anything else on the page
 *   • Asymmetric "Nº01 — BRAND" stamp on the left using the same chapter-number
 *     typography the rest of the deck uses
 *   • Ghost echo of the word "everything" behind the foreground copy
 *   • Concentric-ring decoration (the same style as the ROI score card)
 *   • Hairline lime divider and muted cyan kicker, exactly as elsewhere
 *
 * No new fonts, no new palette. Just scale, whitespace, and hierarchy.
 */
export const BrandManifesto = ({ clientName = "Your Business", preparedDate = null }) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative overflow-hidden rounded-3xl border border-cyan-300/30 bg-[#0a3d54]"
      data-testid="brand-manifesto"
    >
      {/* On-brand corner glows — lime top-right, cyan bottom-left, same as chapters */}
      <div className="absolute top-0 right-0 w-[520px] h-[300px] bg-[radial-gradient(ellipse_at_top_right,rgba(163,230,53,0.18),transparent_70%)] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[380px] h-[240px] bg-[radial-gradient(ellipse_at_bottom_left,rgba(34,211,238,0.14),transparent_70%)] pointer-events-none" />

      {/* Concentric rings decoration — top-right corner */}
      <svg
        aria-hidden
        className="absolute -top-20 -right-20 w-[320px] h-[320px] pointer-events-none opacity-30"
        viewBox="0 0 320 320"
        fill="none"
      >
        <circle cx="160" cy="160" r="152" stroke="rgba(163,230,53,0.25)" strokeWidth="1" />
        <circle cx="160" cy="160" r="116" stroke="rgba(163,230,53,0.20)" strokeWidth="1" />
        <circle cx="160" cy="160" r="80"  stroke="rgba(163,230,53,0.15)" strokeWidth="1" />
        <circle cx="160" cy="160" r="44"  stroke="rgba(163,230,53,0.10)" strokeWidth="1" />
      </svg>

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 p-6 md:p-10 lg:p-14">
        {/* Public-only "Prepared for" eyebrow — only renders when a preparedDate is passed */}
        {preparedDate && (
          <div className="lg:col-span-12 -mb-2" data-testid="prepared-for-eyebrow">
            <p className="text-lime-300 text-[11px] md:text-xs font-bold uppercase tracking-[0.3em]">
              Prepared for {clientName} · {preparedDate}
            </p>
          </div>
        )}

        {/* LEFT: Nº01 stamp — same numeric treatment as chapter numbers */}
        <div className="lg:col-span-4 flex lg:block items-baseline gap-4 lg:gap-0 order-1">
          <div className="relative">
            <p className="text-lime-300 text-[11px] md:text-xs font-bold uppercase tracking-[0.3em] mb-2 lg:mb-3">
              Nº01 · The Case For Your Brand
            </p>
            <div className="h-px w-16 bg-lime-300/60 mb-4 lg:mb-6 hidden lg:block" />
            <p className="hidden lg:block text-cyan-100/60 text-sm leading-relaxed max-w-[220px]">
              A short note, written for <span className="text-white font-medium">{clientName}</span>.
            </p>
          </div>
        </div>

        {/* RIGHT: the manifesto text */}
        <div className="lg:col-span-8 relative order-2">
          <p className="text-cyan-200/70 text-[11px] md:text-xs font-bold uppercase tracking-[0.3em] mb-5">
            Why Your Brand Matters
          </p>

          {/* Opening line */}
          <h3 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.15] mb-2">
            Your brand is more than a logo.
          </h3>

          {/* "It's everything." — massive headline moment */}
          <div className="relative mb-6 md:mb-8">
            {/* Ghost echo — faint oversized word behind */}
            <span
              aria-hidden
              className="absolute -top-4 md:-top-8 -left-2 text-[5rem] md:text-[9rem] lg:text-[11rem] font-black leading-none tracking-tight pointer-events-none select-none whitespace-nowrap"
              style={{ color: "rgba(163,230,53,0.05)" }}
            >
              everything.
            </span>
            <h3 className="relative text-2xl md:text-4xl lg:text-5xl font-bold leading-[1.15] flex flex-wrap items-baseline gap-x-2 md:gap-x-3">
              <span className="text-white">It's</span>
              <motion.span
                initial={{ opacity: 0, letterSpacing: "0.3em" }}
                whileInView={{ opacity: 1, letterSpacing: "-0.01em" }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                className="text-lime-300 font-black italic"
              >
                everything.
              </motion.span>
            </h3>
          </div>

          {/* Body copy */}
          <p className="text-slate-200 text-sm md:text-base lg:text-lg leading-relaxed max-w-2xl mb-5">
            It's the sum of how people perceive your business: your reputation, your customer
            service, your advertising, and the feeling people get when they hear your name.
          </p>

          {/* Hairline divider */}
          <div className="h-px w-24 bg-lime-300/40 mb-5" />

          {/* Closing line — same weight, larger/tighter to punctuate */}
          <p className="text-white text-base md:text-lg lg:text-xl font-semibold leading-snug max-w-2xl">
            When these work together, your brand becomes your most valuable asset.
          </p>

          {/* Mobile-only provenance */}
          <p className="lg:hidden text-cyan-100/60 text-xs mt-6 pt-4 border-t border-cyan-300/15">
            A short note, written for <span className="text-white font-medium">{clientName}</span>.
          </p>
        </div>
      </div>
    </motion.section>
  );
};
